import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Plus, Trash2 } from 'lucide-react';
import BottomSheet from '../../../ui/BottomSheet';
import Form from '../../../ui/Form';
import Input from '../../../ui/Input';
import Badge from '../../../ui/Badge';
import Tooltip from '../../../ui/Tooltip';
import SectionHeader from '../../../ui/SectionHeader';
import { escapeWouldClearField } from '../../../ui/clearOnEscape';
import { getPlayerColor } from '../utils/playerUtils';
import {
  COUNTER_ICONS, COUNTER_NAME_MAX_LENGTH, DEFAULT_COUNTER_ICON, PRESET_COUNTERS, counterIcon,
} from '../utils/counterIcons';
import './ValuesPopover.css';

const COUNTER_MAX = 999;
const COMMANDER_DAMAGE_MAX = 99;

/**
 * Werte-Aufklapper eines Teilnehmers (Spec 2026-10-21 §5.6): Marken und erhaltener
 * Commander-Schaden je Quelle.
 *
 *   <ValuesPopover open={open} onOpenChange={setOpen} player={p} isOwn
 *     damageSources={others} isMobile={isMobile}
 *     onCounterChange={updatePlayerCounter} onCounterRemove={removePlayerCounter}
 *     onCounterAdd={updatePlayerCounter} onCommanderDamageChange={updateCommanderDamage}>
 *     <ParticipantHead … />
 *   </ValuesPopover>
 *
 * - Rechner: Radix-Popover am Element `children` (Anker; das Kind muss seine `ref` an ein
 *   DOM-Element weiterreichen). Handy (`isMobile`): `BottomSheet`, `children` bleiben
 *   unverändert stehen.
 * - Marken: je Zeile Symbol, Name, Stepper und Entfernen. `onCounterChange({ counterId, delta })`,
 *   `onCounterRemove(counterId)`.
 * - „Marke hinzufügen": Vorgaben, die noch fehlen, als Pillen; eigene Marke mit Symbol aus
 *   `COUNTER_ICONS` und Name (höchstens `COUNTER_NAME_MAX_LENGTH` Zeichen).
 *   `onCounterAdd({ counterId, name, icon, value: 1 })` — dieselbe Form wie `updatePlayerCounter`
 *   des Socket-Hooks. Eine neue Marke startet bei 1: der Kopf blendet Marken mit 0 aus.
 * - Commander-Schaden: eine Zeile je Eintrag in `damageSources` (`{ userId, displayName,
 *   position }`); ohne Quellen entfällt der Abschnitt. `onCommanderDamageChange(sourceUserId, delta)`.
 * - Nur `isOwn` darf ändern; sonst zeigt der Aufklapper dieselben Werte nur lesend.
 */
export default function ValuesPopover({
  open, onOpenChange, player, isOwn = false, damageSources = [], isMobile = false,
  onCounterChange, onCounterRemove, onCounterAdd, onCommanderDamageChange, children,
}) {
  const title = `Werte · ${player.displayName}`;
  const body = (
    <ValuesBody
      player={player}
      isOwn={isOwn}
      damageSources={damageSources}
      onCounterChange={onCounterChange}
      onCounterRemove={onCounterRemove}
      onCounterAdd={onCounterAdd}
      onCommanderDamageChange={onCommanderDamageChange}
    />
  );

  if (isMobile) {
    return (
      <>
        {children}
        <BottomSheet open={open} onOpenChange={onOpenChange} title={title}>{body}</BottomSheet>
      </>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Anchor asChild>{children}</Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          className="values-popover"
          sideOffset={6}
          align="start"
          collisionPadding={8}
          aria-label={title}
          onEscapeKeyDown={(event) => {
            if (escapeWouldClearField()) event.preventDefault();
          }}
        >
          <div className="values-popover__title">{title}</div>
          {body}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function ValuesBody({
  player, isOwn, damageSources, onCounterChange, onCounterRemove, onCounterAdd, onCommanderDamageChange,
}) {
  const counters = player.counters || [];
  return (
    <div className="values-popover__body">
      <section className="values-popover__section">
        <SectionHeader size="sm">Marken</SectionHeader>
        {counters.length === 0 && <p className="values-popover__empty">Keine Marken.</p>}
        {counters.map((counter) => (
          <CounterRow
            key={counter.id}
            counter={counter}
            isOwn={isOwn}
            onCounterChange={onCounterChange}
            onCounterRemove={onCounterRemove}
          />
        ))}
        {isOwn && <AddCounter counters={counters} onCounterAdd={onCounterAdd} />}
      </section>

      {damageSources.length > 0 && (
        <section className="values-popover__section">
          <SectionHeader size="sm">Commander-Schaden</SectionHeader>
          {damageSources.map((source) => {
            const value = player.commanderDamage?.[source.userId] ?? 0;
            const label = `Commander-Schaden von ${source.displayName}`;
            return (
              <div key={source.userId} className="values-popover__row" data-testid={`commander-damage-${source.userId}`}>
                <span
                  className="values-popover__dot"
                  style={{ '--seat-color': getPlayerColor(source.position) }}
                  aria-hidden
                />
                <span className="values-popover__name">{source.displayName}</span>
                {isOwn ? (
                  <Form.Stepper
                    value={value}
                    min={0}
                    max={COMMANDER_DAMAGE_MAX}
                    ariaLabel={label}
                    onChange={(next) => onCommanderDamageChange?.(source.userId, next - value)}
                  />
                ) : (
                  <span className="values-popover__value" role="group" aria-label={label}>{value}</span>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

function CounterRow({ counter, isOwn, onCounterChange, onCounterRemove }) {
  const Icon = counterIcon(counter.icon);
  return (
    <div className="values-popover__row" data-testid="values-counter" data-counter-id={counter.id}>
      <Icon size={16} className="values-popover__icon" aria-hidden />
      <span className="values-popover__name">{counter.name}</span>
      {isOwn ? (
        <>
          <Form.Stepper
            value={counter.value}
            min={0}
            max={COUNTER_MAX}
            ariaLabel={counter.name}
            onChange={(next) => onCounterChange?.({ counterId: counter.id, delta: next - counter.value })}
          />
          <Tooltip label="Marke entfernen">
            <button
              type="button"
              className="btn-icon values-popover__remove"
              aria-label={`${counter.name} entfernen`}
              onClick={() => onCounterRemove?.(counter.id)}
            >
              <Trash2 size={14} aria-hidden />
            </button>
          </Tooltip>
        </>
      ) : (
        <span className="values-popover__value">{counter.value}</span>
      )}
    </div>
  );
}

function AddCounter({ counters, onCounterAdd }) {
  const [icon, setIcon] = useState(DEFAULT_COUNTER_ICON);
  const [name, setName] = useState('');
  const missingPresets = PRESET_COUNTERS.filter((preset) => !counters.some((c) => c.id === preset.id));
  const trimmed = name.trim();

  const addCustom = (e) => {
    e.preventDefault();
    if (!trimmed) return;
    onCounterAdd?.({ counterId: newCounterId(), name: trimmed, icon, value: 1 });
    setName('');
    setIcon(DEFAULT_COUNTER_ICON);
  };

  return (
    <div className="values-popover__add">
      <SectionHeader size="sm">Marke hinzufügen</SectionHeader>
      {missingPresets.length > 0 && (
        <div className="values-popover__presets">
          {missingPresets.map((preset) => {
            const Icon = counterIcon(preset.icon);
            return (
              <Badge
                key={preset.id}
                aria-label={`${preset.name} hinzufügen`}
                onClick={() => onCounterAdd?.({ counterId: preset.id, name: preset.name, icon: preset.icon, value: 1 })}
              >
                <Icon size={12} aria-hidden /> {preset.name}
              </Badge>
            );
          })}
        </div>
      )}
      <form className="values-popover__custom" onSubmit={addCustom}>
        <div className="values-popover__icons" role="group" aria-label="Symbol der Marke">
          {Object.entries(COUNTER_ICONS).map(([key, Icon]) => (
            <button
              key={key}
              type="button"
              className="values-popover__icon-choice"
              aria-label={`Symbol ${key}`}
              aria-pressed={icon === key}
              onClick={() => setIcon(key)}
            >
              <Icon size={16} aria-hidden />
            </button>
          ))}
        </div>
        <div className="values-popover__custom-row">
          <Input
            size="sm"
            value={name}
            maxLength={COUNTER_NAME_MAX_LENGTH}
            placeholder="Name der Marke"
            aria-label="Name der Marke"
            onChange={(e) => setName(e.target.value)}
            onClear={() => setName('')}
          />
          <button
            type="submit"
            className="btn-secondary values-popover__add-btn"
            aria-label="Eigene Marke hinzufügen"
            disabled={!trimmed}
          >
            <Plus size={14} aria-hidden /> Hinzufügen
          </button>
        </div>
      </form>
    </div>
  );
}

/** Eigene Marken brauchen eine Kennung, die der Server noch nicht kennt; er nimmt sie wörtlich. */
function newCounterId() {
  return `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}
